import type { Project, TranscriptRevision } from '../types/portal'

/**
 * Transcript text and its saved revisions are kept in browser storage so
 * corrections — and the versions they replaced — are still there when the
 * project is reopened. A real build would store these against the deliverable
 * server-side; nothing here is ever overwritten in place.
 */
const KEY = 'portal.transcripts'
const REVISIONS_KEY = 'portal.transcriptRevisions'

type TranscriptMap = Record<string, string>
type RevisionMap = Record<string, TranscriptRevision[]>

function readJson<T>(key: string): T | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable — edits stay for this session only */
  }
}

export function saveTranscriptText(
  deliverableId: string,
  text: string,
  revisions: TranscriptRevision[],
): void {
  writeJson(KEY, { ...(readJson<TranscriptMap>(KEY) ?? {}), [deliverableId]: text })
  writeJson(REVISIONS_KEY, {
    ...(readJson<RevisionMap>(REVISIONS_KEY) ?? {}),
    [deliverableId]: revisions,
  })
}

/** Re-applies saved transcript text and revision history over the seeded content. */
export function applyStoredTranscripts(projects: Project[]): Project[] {
  const text = readJson<TranscriptMap>(KEY) ?? {}
  const revisions = readJson<RevisionMap>(REVISIONS_KEY) ?? {}
  if (!Object.keys(text).length && !Object.keys(revisions).length) return projects

  return projects.map((project) => ({
    ...project,
    deliverables: project.deliverables.map((item) => {
      if (text[item.id] === undefined && revisions[item.id] === undefined) return item
      return {
        ...item,
        transcript: text[item.id] ?? item.transcript,
        transcriptRevisions: revisions[item.id] ?? item.transcriptRevisions,
      }
    }),
  }))
}

/** Downloads the transcript as a plain text file, named after the deliverable. */
export function downloadTranscript(name: string, text: string): void {
  if (typeof window === 'undefined') return
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${name.replace(/[^\w\s-]/g, '').trim() || 'Transcript'}.txt`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/** How many distinct edits a diff represents, for "3 changes". */
export function countChanges(parts: DiffPart[]): number {
  let count = 0
  let inRun = false
  for (const part of parts) {
    if (part.kind === 'same') {
      // Whitespace between two edits shouldn't split them into separate changes.
      if (part.text.trim()) inRun = false
      continue
    }
    if (!inRun) {
      count += 1
      inRun = true
    }
  }
  return count
}

export interface DiffPart {
  kind: 'same' | 'added' | 'removed'
  text: string
}

/**
 * Word-level diff between two transcript states, via a longest-common-
 * subsequence walk. Deliberately simple: enough to see what a colleague
 * changed, not a document-comparison tool.
 */
export function diffWords(before: string, after: string): DiffPart[] {
  const left = before.split(/(\s+)/).filter(Boolean)
  const right = after.split(/(\s+)/).filter(Boolean)

  const rows = left.length
  const cols = right.length
  const table: number[][] = Array.from({ length: rows + 1 }, () => new Array(cols + 1).fill(0))

  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = cols - 1; j >= 0; j -= 1) {
      table[i][j] =
        left[i] === right[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1])
    }
  }

  const parts: DiffPart[] = []
  const push = (kind: DiffPart['kind'], text: string) => {
    const last = parts[parts.length - 1]
    if (last && last.kind === kind) last.text += text
    else parts.push({ kind, text })
  }

  let i = 0
  let j = 0
  while (i < rows && j < cols) {
    if (left[i] === right[j]) {
      push('same', left[i])
      i += 1
      j += 1
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push('removed', left[i])
      i += 1
    } else {
      push('added', right[j])
      j += 1
    }
  }
  while (i < rows) {
    push('removed', left[i])
    i += 1
  }
  while (j < cols) {
    push('added', right[j])
    j += 1
  }

  return parts
}
